import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  Building2,
  Check,
  CheckCircle2,
  ClipboardList,
  Copy,
  FileSpreadsheet,
  HardHat,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { APP_NAME } from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Role, Zakazka } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { Btn, Chyba, Input, Nacitani, Pole } from '../components/ui.js';
import { sledujUdalost } from '../lib/analytika.js';

/**
 * Onboarding po registraci (/vitejte) — tři kroky k připravené firmě:
 * 1. první zakázka, 2. tým (s vygenerovanými hesly k předání), 3. hotovo.
 * Každý krok jde přeskočit — nic tu není povinné, všechno jde udělat později.
 * Levý navy sloupec drží orientaci (kde jsem, co mě čeká).
 */

type Krok = 1 | 2 | 3;

const KROKY: { cislo: Krok; nazev: string; Ikona: typeof Building2 }[] = [
  { cislo: 1, nazev: 'První zakázka', Ikona: ClipboardList },
  { cislo: 2, nazev: 'Váš tým', Ikona: Users },
  { cislo: 3, nazev: 'Hotovo', Ikona: CheckCircle2 },
];

export function OnboardingPage() {
  const { me, nacitani } = useAuth();
  const navigate = useNavigate();
  const [krok, setKrok] = useState<Krok>(1);
  const [zakazka, setZakazka] = useState<Zakazka | null>(null);

  // Těsně po registraci může být v cache ještě chvilku stale null (me se
  // propisuje o tick později) — null na login potvrzujeme až po prodlevě,
  // jinak by čerstvá firma prolétla přes /login rovnou na Přehled bez uvítání.
  const [odhlasenPotvrzen, setOdhlasenPotvrzen] = useState(false);
  useEffect(() => {
    if (me !== null) {
      setOdhlasenPotvrzen(false);
      return;
    }
    const t = setTimeout(() => setOdhlasenPotvrzen(true), 100);
    return () => clearTimeout(t);
  }, [me]);

  if (nacitani || (me === null && !odhlasenPotvrzen)) {
    return <Nacitani text="Ověřuji přihlášení…" />;
  }
  if (!me) return <Navigate to="/login" replace />;

  return (
    <main className="min-h-screen bg-bg lg:grid lg:grid-cols-[360px_1fr]">
      {/* Orientace: kde v průvodci jsem. */}
      <aside className="flex flex-col gap-10 bg-primary p-6 text-white lg:min-h-screen lg:p-10">
        <div className="flex items-center gap-2.5">
          <span className="flex size-9 items-center justify-center rounded-lg bg-accent text-text-heading">
            <HardHat size={20} strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="text-lg font-bold tracking-widest uppercase">{APP_NAME}</span>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-balance text-white">
            Vítejte, {krestni(me.jmeno)}.
          </h1>
          <p className="mt-2 text-base text-primary-tint">
            Firma je založená. Teď ji za pár minut připravíme na první stavbu.
          </p>
        </div>

        <ol className="flex flex-col gap-1" aria-label="Kroky průvodce">
          {KROKY.map((k) => {
            const hotovo = krok > k.cislo;
            const aktivni = krok === k.cislo;
            return (
              <li
                key={k.cislo}
                aria-current={aktivni ? 'step' : undefined}
                className={`flex min-h-12 items-center gap-3 rounded-lg px-3 ${
                  aktivni ? 'bg-white/10 font-bold' : 'text-primary-tint'
                }`}
              >
                <span
                  className={`flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                    hotovo
                      ? 'bg-success text-text-heading'
                      : aktivni
                        ? 'bg-accent text-text-heading'
                        : 'bg-white/10'
                  }`}
                >
                  {hotovo ? <Check size={16} strokeWidth={2.5} aria-hidden="true" /> : k.cislo}
                </span>
                {k.nazev}
              </li>
            );
          })}
        </ol>

        <p className="mt-auto hidden text-sm text-primary-tint lg:block">
          Všechno tu jde přeskočit a doplnit později — nic není zamčené.
        </p>
      </aside>

      {/* Obsah kroku. */}
      <section className="flex flex-col items-center p-6 lg:justify-center lg:p-12">
        {krok === 1 && (
          <KrokZakazka
            naDalsi={(z) => {
              setZakazka(z);
              setKrok(2);
            }}
          />
        )}
        {krok === 2 && <KrokTym naDalsi={() => setKrok(3)} />}
        {krok === 3 && (
          <KrokHotovo
            zakazka={zakazka}
            naKonec={() => {
              sledujUdalost('onboarding_dokoncen');
              void navigate('/prehled', { replace: true });
            }}
          />
        )}
      </section>
    </main>
  );
}

function krestni(jmeno: string): string {
  return jmeno.split(' ')[0] ?? jmeno;
}

/** Obal kroku: ikona v chipu, nadpis, podtitulek, obsah. */
function KrokObal({
  ikona,
  titulek,
  podtitulek,
  children,
}: {
  ikona: ReactNode;
  titulek: string;
  podtitulek: string;
  children: ReactNode;
}) {
  return (
    <div className="w-full max-w-xl">
      <span className="flex size-12 items-center justify-center rounded-xl bg-primary-tint text-primary">
        {ikona}
      </span>
      <h2 className="mt-4 text-2xl font-bold text-balance">{titulek}</h2>
      <p className="mt-1 max-w-prose text-base text-text-secondary">{podtitulek}</p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Krok 1 — první zakázka
// ---------------------------------------------------------------------------

function KrokZakazka({ naDalsi }: { naDalsi: (z: Zakazka | null) => void }) {
  const [cislo, setCislo] = useState('');
  const [nazev, setNazev] = useState('');
  const [lokalita, setLokalita] = useState('');
  const [chyba, setChyba] = useState<string | null>(null);

  const zaloz = useMutation({
    mutationFn: () =>
      api.post<Zakazka>('/api/projects', {
        cislo,
        nazev,
        lokalita: lokalita.trim() || null,
      }),
    onSuccess: (z) => {
      sledujUdalost('onboarding_zakazka_zalozena');
      naDalsi(z);
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <KrokObal
      ikona={<ClipboardList size={24} strokeWidth={1.75} />}
      titulek="Založte první zakázku"
      podtitulek="Stavba, na které to celé poběží — deník, výkresy i vícepráce se na ni zavěsí."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          zaloz.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
          <Pole popisek="Číslo" povinne>
            <Input
              name="cislo"
              required
              placeholder="Z-2026-001"
              value={cislo}
              onChange={(e) => setCislo(e.target.value)}
            />
          </Pole>
          <Pole popisek="Název stavby" povinne>
            <Input
              name="nazev"
              required
              placeholder="Bytový dům Slavonín"
              value={nazev}
              onChange={(e) => setNazev(e.target.value)}
            />
          </Pole>
        </div>
        <Pole popisek="Lokalita">
          <Input name="lokalita" value={lokalita} onChange={(e) => setLokalita(e.target.value)} />
        </Pole>
        <Chyba>{chyba}</Chyba>
        <div className="mt-2 flex items-center justify-between gap-3">
          <button
            type="button"
            className="min-h-11 rounded-md px-2 text-sm font-semibold text-text-secondary hover:text-text-primary"
            onClick={() => naDalsi(null)}
          >
            Přeskočit — založím později
          </button>
          <Btn type="submit" disabled={zaloz.isPending}>
            {zaloz.isPending ? 'Zakládám…' : 'Založit a pokračovat'}
          </Btn>
        </div>
      </form>
    </KrokObal>
  );
}

// ---------------------------------------------------------------------------
// Krok 2 — tým (účty s vygenerovaným heslem k předání)
// ---------------------------------------------------------------------------

interface NovyClen {
  jmeno: string;
  email: string;
  roleId: string;
}

interface ZalozenyClen extends NovyClen {
  roleNazev: string;
  heslo: string;
}

/** Čitelné dočasné heslo (písmena + číslice, splní pravidla API). */
function vygenerujHeslo(): string {
  const cisla = new Uint32Array(2);
  crypto.getRandomValues(cisla);
  const slova = ['Stavba', 'Jerab', 'Beton', 'Krokev', 'Leseni', 'Cihla'];
  const slovo = slova[(cisla[0] ?? 0) % slova.length] ?? 'Stavba';
  return `${slovo}-${String((cisla[1] ?? 0) % 10000).padStart(4, '0')}`;
}

function KrokTym({ naDalsi }: { naDalsi: () => void }) {
  const queryClient = useQueryClient();
  const [radky, setRadky] = useState<NovyClen[]>([{ jmeno: '', email: '', roleId: '' }]);
  const [zalozeni, setZalozeni] = useState<ZalozenyClen[]>([]);
  const [chyba, setChyba] = useState<string | null>(null);
  const [zkopirovano, setZkopirovano] = useState(false);

  const { data: role } = useQuery({
    queryKey: ['role'],
    queryFn: () => api.get<Role[]>('/api/roles'),
  });
  // Ředitelem je zakladatel — kolegům se nabízí terén/kancelář role.
  const nabidka = (role ?? []).filter((r) => r.nazev !== 'ředitel');

  const zaloz = useMutation({
    mutationFn: async () => {
      const vyplnene = radky.filter((r) => r.jmeno.trim() && r.email.trim() && r.roleId);
      const vysledky: ZalozenyClen[] = [];
      for (const r of vyplnene) {
        const heslo = vygenerujHeslo();
        await api.post('/api/users', {
          jmeno: r.jmeno.trim(),
          email: r.email.trim(),
          heslo,
          roleIds: [r.roleId],
        });
        vysledky.push({
          ...r,
          heslo,
          roleNazev: nabidka.find((n) => n.id === r.roleId)?.nazev ?? '',
        });
      }
      return vysledky;
    },
    onSuccess: (vysledky) => {
      sledujUdalost('onboarding_ucty_zalozeny', { pocet: vysledky.length });
      void queryClient.invalidateQueries({ queryKey: ['uzivatele'] });
      setZalozeni((z) => [...z, ...vysledky]);
      setRadky([{ jmeno: '', email: '', roleId: '' }]);
      setChyba(null);
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  const zkopirujPristupy = async () => {
    const text = zalozeni
      .map((c) => `${c.jmeno} (${c.roleNazev}) — e-mail: ${c.email}, heslo: ${c.heslo}`)
      .join('\n');
    await navigator.clipboard.writeText(text);
    setZkopirovano(true);
    setTimeout(() => setZkopirovano(false), 3000);
  };

  const maCoZalozit = radky.some((r) => r.jmeno.trim() && r.email.trim() && r.roleId);

  return (
    <KrokObal
      ikona={<Users size={24} strokeWidth={1.75} />}
      titulek="Pozvěte svůj tým"
      podtitulek="Mistr uvidí deník a nálezy, přípravář peníze — každá role jen to své. Hesla vygenerujeme, vy je předáte."
    >
      <div className="flex flex-col gap-4">
        {radky.map((r, i) => (
          <div
            key={i}
            className="grid items-end gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-[1fr_1fr_10rem_auto]"
          >
            <Pole popisek="Jméno">
              <Input
                aria-label={`Jméno člena ${i + 1}`}
                value={r.jmeno}
                onChange={(e) =>
                  setRadky((d) => d.map((x, j) => (j === i ? { ...x, jmeno: e.target.value } : x)))
                }
              />
            </Pole>
            <Pole popisek="E-mail">
              <Input
                type="email"
                aria-label={`E-mail člena ${i + 1}`}
                value={r.email}
                onChange={(e) =>
                  setRadky((d) => d.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))
                }
              />
            </Pole>
            <Pole popisek="Role">
              <select
                aria-label={`Role člena ${i + 1}`}
                className="min-h-10 w-full rounded-md border border-border bg-surface px-2 text-base focus:border-primary"
                value={r.roleId}
                onChange={(e) =>
                  setRadky((d) => d.map((x, j) => (j === i ? { ...x, roleId: e.target.value } : x)))
                }
              >
                <option value="">Vyberte…</option>
                {nabidka.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.nazev}
                  </option>
                ))}
              </select>
            </Pole>
            {radky.length > 1 ? (
              <button
                type="button"
                aria-label={`Odebrat člena ${i + 1}`}
                className="flex min-h-10 min-w-10 items-center justify-center self-end rounded-md text-text-secondary hover:bg-bg hover:text-error-text"
                onClick={() => setRadky((d) => d.filter((_, j) => j !== i))}
              >
                <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
              </button>
            ) : (
              <span />
            )}
          </div>
        ))}

        <button
          type="button"
          className="flex min-h-11 items-center gap-1.5 self-start rounded-md px-2 text-sm font-semibold text-primary hover:bg-primary-tint"
          onClick={() => setRadky((d) => [...d, { jmeno: '', email: '', roleId: '' }])}
        >
          <Plus size={16} strokeWidth={2} aria-hidden="true" /> Další člen
        </button>

        {zalozeni.length > 0 && (
          <div className="rounded-xl border border-success-border bg-success-bg p-4">
            <p className="flex items-center gap-2 font-semibold text-success-text">
              <CheckCircle2 size={18} strokeWidth={2} aria-hidden="true" />
              Účty založené — předejte přístupy:
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-base">
              {zalozeni.map((c) => (
                <li key={c.email} className="tabular-nums">
                  <span className="font-semibold">{c.jmeno}</span> ({c.roleNazev}) — {c.email} ·
                  heslo: <code className="rounded bg-surface px-1.5 py-0.5">{c.heslo}</code>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="mt-3 flex min-h-10 items-center gap-1.5 rounded-md border border-success-border bg-surface px-3 text-sm font-semibold text-success-text hover:bg-success-bg"
              onClick={() => void zkopirujPristupy()}
            >
              {zkopirovano ? (
                <>
                  <Check size={16} strokeWidth={2} aria-hidden="true" /> Zkopírováno
                </>
              ) : (
                <>
                  <Copy size={16} strokeWidth={2} aria-hidden="true" /> Zkopírovat přístupy
                </>
              )}
            </button>
            <p className="mt-2 text-sm text-success-text">
              Heslo si každý změní v Menu → Změnit heslo. Tady ho vidíte naposledy.
            </p>
          </div>
        )}

        <Chyba>{chyba}</Chyba>
        <div className="mt-2 flex items-center justify-between gap-3">
          <button
            type="button"
            className="min-h-11 rounded-md px-2 text-sm font-semibold text-text-secondary hover:text-text-primary"
            onClick={naDalsi}
          >
            {zalozeni.length > 0 ? 'Pokračovat' : 'Přeskočit — pozvu později'}
          </button>
          <Btn onClick={() => zaloz.mutate()} disabled={!maCoZalozit || zaloz.isPending}>
            {zaloz.isPending ? 'Zakládám účty…' : 'Založit účty'}
          </Btn>
        </div>
      </div>
    </KrokObal>
  );
}

// ---------------------------------------------------------------------------
// Krok 3 — hotovo, kam dál
// ---------------------------------------------------------------------------

function KrokHotovo({ zakazka, naKonec }: { zakazka: Zakazka | null; naKonec: () => void }) {
  return (
    <KrokObal
      ikona={<CheckCircle2 size={24} strokeWidth={1.75} />}
      titulek="Firma je připravená."
      podtitulek={
        zakazka
          ? `Zakázka ${zakazka.cislo} — ${zakazka.nazev} čeká na první zápis.`
          : 'Zakázku založíte kdykoli v sekci Zakázky.'
      }
    >
      <ul className="flex flex-col gap-3">
        <DalsiKrok
          ikona={<BookOpen size={18} strokeWidth={1.75} />}
          titulek="Deník píše mistr v mobilu"
        >
          Předejte mu přístup — den nadiktuje, vyfotí a večer podepíše.
        </DalsiKrok>
        <DalsiKrok
          ikona={<FileSpreadsheet size={18} strokeWidth={1.75} />}
          titulek="Nahrajte soupis prací ke smlouvě"
        >
          Ceník položek pak oceňuje vícepráce jedním kliknutím.
        </DalsiKrok>
        <DalsiKrok
          ikona={<AlertTriangle size={18} strokeWidth={1.75} />}
          titulek="Nálezy hlídají peníze"
        >
          Co terén vyfotí, přistane vám ve frontě — rozhodnete, co je vícepráce.
        </DalsiKrok>
      </ul>
      <Btn className="mt-8 w-full" onClick={naKonec}>
        Otevřít Přehled
      </Btn>
    </KrokObal>
  );
}

function DalsiKrok({
  ikona,
  titulek,
  children,
}: {
  ikona: ReactNode;
  titulek: string;
  children: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 rounded-xl border border-border bg-surface p-4">
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-tint text-primary">
        {ikona}
      </span>
      <span className="min-w-0">
        <span className="block font-bold">{titulek}</span>
        <span className="block text-base text-text-secondary">{children}</span>
      </span>
    </li>
  );
}
